import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { refundService } from '../services/refundService';
import ConfirmDialog from '../components/ConfirmDialog';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import NotificationBell from '../components/NotificationBell';

const Refunds = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, onConfirm: null, title: '', message: '', type: 'warning' });
  const [filters, setFilters] = useState({
    status: '',
    page: 1,
    limit: 20,
  });
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [createForm, setCreateForm] = useState({
    orderId: '',
    reason: '',
    description: '',
    amount: '',
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadRefunds();
  }, [user, filters]);

  const loadRefunds = async () => {
    try {
      setLoading(true);
      const response = await refundService.getAllRefunds(filters);
      if (response.success) {
        setRefunds(response.refunds || []);
        setPagination({
          total: response.total || 0,
          totalPages: response.totalPages || 0,
        });
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRefund = async (e) => {
    e.preventDefault();
    try {
      setCreating(true);
      const response = await refundService.createRefundRequest(
        createForm.orderId,
        createForm.reason,
        createForm.description,
        createForm.amount ? parseFloat(createForm.amount) : undefined
      );
      if (response.success) {
        toast.success('Demande de remboursement créée avec succès');
        setShowCreateModal(false);
        setCreateForm({ orderId: '', reason: '', description: '', amount: '' });
        loadRefunds();
      } else {
        toast.error(`Erreur: ${response.error}`);
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Une erreur est survenue');
    } finally {
      setCreating(false);
    }
  };

  const handleCancelRefund = async (refundId) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Annuler la demande',
      message: 'Êtes-vous sûr de vouloir annuler cette demande de remboursement ?',
      type: 'warning',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        try {
          const response = await refundService.cancelRefund(refundId);
          if (response.success) {
            toast.success('Demande de remboursement annulée');
            loadRefunds();
          } else {
            toast.error(`Erreur: ${response.error}`);
          }
        } catch (error) {
          console.error('Erreur:', error);
          toast.error('Une erreur est survenue');
        }
      },
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#ffc107',
      approved: '#28a745',
      rejected: '#dc3545',
      processed: '#17a2b8',
      cancelled: '#6c757d',
    };
    return colors[status] || '#6c757d';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      approved: 'Approuvé',
      rejected: 'Rejeté',
      processed: 'Traité',
      cancelled: 'Annulé',
    };
    return labels[status] || status;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div>
      <Navbar />
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        onConfirm={() => {
          if (confirmDialog.onConfirm) {
            confirmDialog.onConfirm();
          }
          setConfirmDialog({ ...confirmDialog, isOpen: false });
        }}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />

      <div className="container" style={{ maxWidth: '1200px', margin: '40px auto', padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <h1 style={{ color: '#FF0000', fontSize: '32px', fontWeight: '700', margin: 0 }}>
            💰 Mes Remboursements
          </h1>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary"
          >
            + Nouvelle demande
          </button>
        </div>

        {/* Filtres */}
        <div className="card" style={{ marginBottom: '20px', padding: '20px' }}>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                Statut
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px',
                }}
              >
                <option value="">Tous</option>
                <option value="pending">En attente</option>
                <option value="approved">Approuvé</option>
                <option value="rejected">Rejeté</option>
                <option value="processed">Traité</option>
                <option value="cancelled">Annulé</option>
              </select>
            </div>
            <div style={{ marginLeft: 'auto', color: '#666', fontSize: '14px' }}>
              Total: <strong>{pagination.total}</strong> demande(s)
            </div>
          </div>
        </div>

        {/* Liste des remboursements */}
        {loading ? (
          <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
            <p>Chargement...</p>
          </div>
        ) : refunds.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ color: '#666', marginBottom: '20px' }}>Aucune demande de remboursement</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn btn-primary"
            >
              Créer une demande
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gap: '15px', marginBottom: '20px' }}>
              {refunds.map((refund) => (
                <div key={refund._id} className="card" style={{ padding: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '15px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '10px' }}>
                        <h3 style={{ margin: 0, color: '#333', fontSize: '18px' }}>
                          Commande: {refund.orderId?.orderNumber || refund.orderId}
                        </h3>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          backgroundColor: getStatusColor(refund.status),
                          color: '#fff',
                          fontSize: '12px',
                          fontWeight: 'bold',
                        }}>
                          {getStatusLabel(refund.status)}
                        </span>
                      </div>
                      <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>
                        <strong>Montant:</strong> {formatCurrency(refund.amount)}
                      </p>
                      <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>
                        <strong>Raison:</strong> {refund.reason}
                      </p>
                      {refund.description && (
                        <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>
                          <strong>Description:</strong> {refund.description}
                        </p>
                      )}
                      {refund.rejectionReason && (
                        <p style={{ margin: '5px 0', color: '#dc3545', fontSize: '14px' }}>
                          <strong>Raison du rejet:</strong> {refund.rejectionReason}
                        </p>
                      )}
                      {refund.stripeRefundId && (
                        <p style={{ margin: '5px 0', color: '#666', fontSize: '12px' }}>
                          <strong>Transaction ID:</strong> {refund.stripeRefundId}
                        </p>
                      )}
                      {refund.mobileMoneyTransactionId && (
                        <p style={{ margin: '5px 0', color: '#666', fontSize: '12px' }}>
                          <strong>Transaction ID:</strong> {refund.mobileMoneyTransactionId}
                        </p>
                      )}
                      <p style={{ margin: '5px 0', color: '#999', fontSize: '12px' }}>
                        Créé le: {formatDate(refund.createdAt)}
                      </p>
                      {refund.processedAt && (
                        <p style={{ margin: '5px 0', color: '#999', fontSize: '12px' }}>
                          Traité le: {formatDate(refund.processedAt)}
                        </p>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
                      {refund.status === 'pending' && (
                        <button
                          onClick={() => handleCancelRefund(refund._id)}
                          className="btn btn-secondary"
                          style={{ fontSize: '14px', whiteSpace: 'nowrap' }}
                        >
                          Annuler
                        </button>
                      )}
                      <Link
                        to={`/tracking/${refund.orderId?._id || refund.orderId}`}
                        className="btn"
                        style={{
                          fontSize: '14px',
                          whiteSpace: 'nowrap',
                          backgroundColor: '#17a2b8',
                          color: '#fff',
                          textDecoration: 'none',
                          display: 'inline-block',
                          textAlign: 'center',
                        }}
                      >
                        Voir la commande
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
                marginTop: '30px',
              }}>
                <button
                  onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={filters.page === 1}
                  className="btn btn-secondary"
                  style={{
                    opacity: filters.page === 1 ? 0.5 : 1,
                    cursor: filters.page === 1 ? 'not-allowed' : 'pointer',
                  }}
                >
                  ← Précédent
                </button>
                <span style={{ color: '#666', fontSize: '14px' }}>
                  Page <strong>{filters.page}</strong> sur <strong>{pagination.totalPages}</strong>
                </span>
                <button
                  onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={filters.page >= pagination.totalPages}
                  className="btn btn-secondary"
                  style={{
                    opacity: filters.page >= pagination.totalPages ? 0.5 : 1,
                    cursor: filters.page >= pagination.totalPages ? 'not-allowed' : 'pointer',
                  }}
                >
                  Suivant →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de création */}
      {showCreateModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
          }}
          onClick={() => setShowCreateModal(false)}
        >
          <div
            className="card"
            style={{
              maxWidth: '600px',
              width: '100%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginBottom: '20px', color: '#FF0000' }}>
              Nouvelle demande de remboursement
            </h2>
            <form onSubmit={handleCreateRefund}>
              <div className="form-group">
                <label htmlFor="orderId">ID de la commande *</label>
                <input
                  type="text"
                  id="orderId"
                  value={createForm.orderId}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, orderId: e.target.value }))}
                  required
                  placeholder="ID de la commande à rembourser"
                />
              </div>
              <div className="form-group">
                <label htmlFor="reason">Raison du remboursement *</label>
                <select
                  id="reason"
                  value={createForm.reason}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, reason: e.target.value }))}
                  required
                >
                  <option value="">Sélectionnez une raison</option>
                  <option value="Produit défectueux">Produit défectueux</option>
                  <option value="Produit non reçu">Produit non reçu</option>
                  <option value="Produit incorrect">Produit incorrect</option>
                  <option value="Commande annulée">Commande annulée</option>
                  <option value="Double paiement">Double paiement</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={createForm.description}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                  rows="4"
                  placeholder="Décrivez votre demande de remboursement..."
                />
              </div>
              <div className="form-group">
                <label htmlFor="amount">Montant (optionnel - laisser vide pour remboursement total)</label>
                <input
                  type="number"
                  id="amount"
                  value={createForm.amount}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, amount: e.target.value }))}
                  step="0.01"
                  min="0"
                  placeholder="Montant à rembourser"
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={creating}
                >
                  {creating ? 'Création...' : 'Créer la demande'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Refunds;

